<template>
    <Page withNavbar>
        <main :class="classes.main">
            <section class="infos">
                <div class="image">
                    <img :src="user.picture"/>
                </div>
                <div class="general">
                    <h4 :style="configStyles.primary.text">
                        {{ config.PROFILE_INFO_GENERAL_TITLE }}
                    </h4>
                    <form>
                        <Input
                            v-model="form.user.lastName"
                            type="text"
                            :label="config.PROFILE_INFO_GENERAL_SURNAME"
                            :placeholder="config.PROFILE_INFO_GENERAL_SURNAME"
                        />
                        <Input
                            v-model="form.user.firstName"
                            type="text"
                            :label="config.PROFILE_INFO_GENERAL_FIRSTNAME"
                            :placeholder="config.PROFILE_INFO_GENERAL_FIRSTNAME"
                        />
                        <Input
                            v-model="form.user.birthdate"
                            type="date"
                            :label="config.PROFILE_INFO_GENERAL_BIRTHDATE"
                            :placeholder="config.PROFILE_INFO_GENERAL_BIRTHDATE"
                        />
                        <Input
                            v-model="form.user.email"
                            type="text"
                            :label="config.PROFILE_INFO_GENERAL_EMAIL"
                            :placeholder="config.PROFILE_INFO_GENERAL_EMAIL"
                        />
                        <InputPhone
                            v-model="form.user.phone"
                            :label="config.PROFILE_INFO_GENERAL_PHONE"
                            :placeholder="config.PROFILE_INFO_GENERAL_PHONE"
                        />
                    </form>
                </div>
                <div class="addresses">
                    <h4 :style="configStyles.primary.text">
                        {{ config.PROFILE_ADDRESSES_TITLE }}
                    </h4>
                    <AddressItem
                        v-for="address in user.addresses"
                        :key="address.id"
                        :address="address"
                        @onEdit="onAddressEdit"
                        @onDelete="onAddressDelete"
                    />
                    <p v-if="!user.addresses.length">
                        {{ config.PROFILE_ADDRESSES_EMPTY }}
                    </p>
                    <Button type="button" class="button" :color="config.COLOR_PRIMARY" @click="onAddressCreate">
                        {{ config.PROFILE_CREATE_ADDRESS }}
                    </Button>
                </div>
                <div class="social">
                    <h4 :style="configStyles.primary.text">
                        {{ config.PROFILE_INFO_SOCIAL_TITLE }}
                    </h4>
                    <form>
                        <Input
                            v-model="form.user.social.instagram"
                            type="text"
                            :label="config.PROFILE_INFO_SOCIAL_INSTAGRAM"
                            :placeholder="config.PROFILE_INFO_SOCIAL_INSTAGRAM"
                        />
                        <Input
                            v-model="form.user.social.youtube"
                            type="text"
                            :label="config.PROFILE_INFO_SOCIAL_YOUTUBE"
                            :placeholder="config.PROFILE_INFO_SOCIAL_YOUTUBE"
                        />
                        <Input
                            v-model="form.user.social.twitter"
                            type="text"
                            :label="config.PROFILE_INFO_SOCIAL_TWITTER"
                            :placeholder="config.PROFILE_INFO_SOCIAL_TWITTER"
                        />
                        <Input
                            v-model="form.user.social.snapchat"
                            type="text"
                            :label="config.PROFILE_INFO_SOCIAL_SNAPCHAT"
                            :placeholder="config.PROFILE_INFO_SOCIAL_SNAPCHAT"
                        />
                        <Input
                            v-model="form.user.social.website"
                            type="text"
                            :label="config.PROFILE_INFO_SOCIAL_BLOG"
                            :placeholder="config.PROFILE_INFO_SOCIAL_BLOG"
                        />
                    </form>
                </div>
            </section>
            <section v-if="showUserVideos" class="videos">
                <h4 :style="configStyles.primary.text">
                    {{ config.PROFILE_VIDEOS_TITLE }} ({{ validatedProjects.length }})
                </h4>
                <ProjectsList :projects="validatedProjects" @click="onProjectClick"/>
            </section>
            <section :class="classes.privacy">
                <div class="trigger" @click="onPrivacy">
                    <h4 :style="configStyles.primary.text">
                        {{ config.PROFILE_PRIVACY_TITLE }}
                    </h4>
                    <ChevronDown :color="config.COLOR_PRIMARY"/>
                </div>
                <TransitionCollapse>
                    <div v-show="privacy" class="content">
                        <template v-if="this.user.canDeleteAccount">
                            <p>
                                {{ config.PROFILE_EDIT_DELETE }}
                            </p>
                            <p>
                                <a :style="configStyles.primary.text" @click="onDelete">
                                    {{ config.PROFILE_EDIT_DELETE_CTA }}
                                </a>
                            </p>
                        </template>
                        <p class="warning-delete" v-if="!this.user.canDeleteAccount">
                            {{ config.PROFILE_EDIT_CANT_DELETE }}
                        </p>
                    </div>
                </TransitionCollapse>
            </section>
            <section class="actions">
                <Button :color="config.COLOR_PRIMARY" :disabled="loading" outline @click="onCancel">
                    {{ config.PROFILE_EDIT_CANCEL }}
                </Button>
                <Button :color="config.COLOR_PRIMARY" :disabled="loading" @click="onSubmit">
                    {{ config.PROFILE_EDIT_SAVE }}
                </Button>
            </section>
        </main>
        <!-- Modals -->
        <transition name="fade">
            <ModalProfileDelete
                v-if="modals.delete"
                @close="onDeleteClose"
                @done="onDeleteDone"
            />
            <ModalProject
                v-if="modals.project"
                :project="modals.project"
                @close="onProjectClose"
            />
            <ModalAddress
                v-if="modals.address"
                :address="form.address"
                @close="onAddressClose"
            />
            <ModalAddressDelete
                v-if="modals.deleteAddress"
                :address="form.address"
                @close="onAddressDeleteClose"
            />
        </transition>
    </Page>
</template>
<script>
import { SolutionConsumer, UserConsumer, ProjectConsumer } from '../../mixins';
import {
    Page, ModalProfileDelete, ModalProject, ModalAddress,
} from '../../components/desktop';
import { ProjectsList, ModalAddressDelete, AddressItem } from '../../components/shared';

export default {
    name: 'Profile',
    components: {
        ModalAddressDelete,
        ModalAddress,
        Page,
        ModalProfileDelete,
        ModalProject,
        ProjectsList,
        AddressItem,
    },
    mixins: [SolutionConsumer, UserConsumer, ProjectConsumer],
    data () {
        return {
            loading: false,
            form: {
                user: {},
                address: {},
            },
            modals: {
                delete: false,
                project: null,
                address: false,
                deleteAddress: false,
            },
            privacy: false,
        };
    },
    computed: {
        classes () {
            return {
                main: {
                    'with-videos': this.showUserVideos,
                    'without-videos': !this.showUserVideos,
                },
                privacy: {
                    privacy: true,
                    open: this.privacy,
                },
            };
        },
    },
    created () {
    // Initialize form from user information
        this.form.user = {
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            email: this.user.email,
            phone: this.user.phone,
            birthdate: this.user.birthdate,
            social: {
                twitter: this.user.social.twitter,
                instagram: this.user.social.instagram,
                youtube: this.user.social.youtube,
                snapchat: this.user.social.snapchat,
                facebook: this.user.social.facebook,
                website: this.user.social.website,
            },
        };
    },
    methods: {
        onPrivacy () {
            this.privacy = !this.privacy;
        },
        onCancel () {
            this.$router.go(-1);
        },
        async onSubmit () {
            try {
                this.loading = true;
                // Update user info
                await this.editUser(this.form.user);
                // Update local copy
                await this.updateUser();
            } catch (err) {
                console.error(err);
            } finally {
                this.loading = false;
            }
        },
        onDelete () {
            this.modals.delete = true;
        },
        onDeleteClose () {
            this.modals.delete = false;
        },
        async onDeleteDone () {
            try {
                this.modals.delete = false;
            } catch (err) {
                console.log(err);
            }
        },
        onProjectClose () {
            this.modals.project = null;
        },
        onProjectClick (project) {
            this.modals.project = project;
        },
        onAddressDelete (address) {
            this.modals.deleteAddress = true;
            this.form.address = address;
        },
        onAddressDeleteClose () {
            this.modals.deleteAddress = false;
            this.form.address = {};
        },
        onAddressCreate () {
            this.modals.address = true;
        },
        onAddressEdit (address = {}) {
            this.modals.address = true;
            this.form.address = address;
        },
        onAddressClose () {
            this.modals.address = false;
            this.form.address = {};
        },
    },
};
</script>
<style lang="scss" scoped>
@import '../../assets/scss/mixins';
@import '../../assets/scss/colors';

main {
  @include container(20px);
  margin: 0 auto;

  > section {
    &.infos {
      @include card(40px);
      grid-area: infos;
      display: grid;
      gap: 40px;

      > .image {
        justify-self: center;

        > img {
          @include profile-picture();
          @include square(100px);
        }
      }

      > .general, > .social {
        > form {
          display: grid;
          gap: 20px 60px;
        }
      }

      > .addresses {
        > .button {
          margin-top: 15px;
        }
      }
    }

    &.videos {
      @include card(20px);
      grid-area: videos;
    }

    &.privacy {
      @include card(20px);
      grid-area: privacy;
      padding: 0 20px;

      > .trigger {
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;

        > h4 {
          margin: 14px 0;
        }

        > svg {
          transition: transform .2s ease-in-out;
        }
      }

      > .content {
        padding-bottom: 20px;

        > p:nth-child(2) {
          text-align: right;
          font-size: 12px;
          font-weight: bold;
          cursor: pointer;
        }
      }

      &.open {
        > .trigger {
          > svg {
            transform: rotate(180deg);
          }
        }
      }
    }

    &.actions {
      grid-area: actions;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 40px 0;

      button {
        margin: 0 20px;
      }
    }
  }

  @include breakpoint-min(xs) {
    width: 100%;

    &.with-videos {
      grid-template-areas:
                'infos'
                'videos'
                'privacy'
                'actions';
    }

    &.without-videos {
      grid-template-areas:
                'infos'
                'privacy'
                'actions';
    }
  }

  @include breakpoint-min(sm) {
    width: 70%;
  }

  @include breakpoint-min(md) {
    &.with-videos {
      width: 100%;
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
                'infos videos'
                'privacy videos'
                'actions actions';
    }
  }

  @include breakpoint-min(lg) {
    &.with-videos {
      width: 80%;
    }

    > section {
      &.infos {
        > .general, > .social {
          > form {
            grid-template-columns: repeat(2, 1fr);
            grid-auto-rows: 1fr;
          }
        }
      }
    }
  }

  @include breakpoint-min(xl) {
    &.with-videos {
      grid-template-columns: 2fr 1fr;
    }
  }
}
</style>
